import {useCallback, useMemo, useState} from 'react';
import {
	DASHBOARD_BASE_DEFAULT_VISIBLE,
	DASHBOARD_COLUMNS,
	DASHBOARD_RESOLVED_VIEW_EXTRA_DEFAULTS,
	type DashboardColumnKey,
} from '../constants/dashboardColumns';
import {usePersistedColumnVisibility, type ColumnVisibility} from '../../tickets/hooks/usePersistedColumnVisibility';

export type UseDashboardColumnVisibility = ColumnVisibility<DashboardColumnKey>;

/**
 * Distinct from the ticket list's key: the two tables show different columns, and
 * sharing a key would mean hiding a column in one silently hid it in the other.
 */
const STORAGE_KEY = 'ticketDashboard.visibleColumns.v1';

/**
 * Column visibility for the dashboard's ticket table.
 *
 * The base defaults are the PRD's 10 visible columns (§8.1). When the view is
 * scoped to resolved tickets, the PRD promotes two more — Resolution time and
 * Tags — because they are the columns that only mean anything once a ticket is
 * done. `resolvedView` is that scope; the route derives it from the applied
 * status filter (`isResolvedScope`).
 *
 * The promotion is deliberately **not persisted**. Only the user's own toggles
 * reach localStorage, so filtering to resolved for a minute cannot permanently
 * rearrange the columns of every later visit. The reader can still hide a
 * promoted column, and that suppression holds for as long as the page is open —
 * a view-driven default that fought the reader's explicit "hide" would be worse
 * than no promotion at all.
 */
export function useDashboardColumnVisibility(resolvedView = false): UseDashboardColumnVisibility {
	const persisted = usePersistedColumnVisibility<DashboardColumnKey>({
		storageKey: STORAGE_KEY,
		columns: DASHBOARD_COLUMNS,
		defaultVisibleKeys: DASHBOARD_BASE_DEFAULT_VISIBLE,
	});

	/** Promoted columns the reader has hidden again. Session-only, by design (see above). */
	const [suppressed, setSuppressed] = useState<ReadonlySet<DashboardColumnKey>>(() => new Set());

	const promoted = useMemo<ReadonlySet<DashboardColumnKey>>(() => {
		if (!resolvedView) return new Set();
		const next = new Set<DashboardColumnKey>();
		for (const key of DASHBOARD_RESOLVED_VIEW_EXTRA_DEFAULTS) {
			if (!suppressed.has(key)) next.add(key);
		}
		return next;
	}, [resolvedView, suppressed]);

	const visible = useMemo<ReadonlySet<DashboardColumnKey>>(() => {
		if (promoted.size === 0) return persisted.visible;
		return new Set<DashboardColumnKey>([...persisted.visible, ...promoted]);
	}, [persisted.visible, promoted]);

	const isVisible = useCallback((key: DashboardColumnKey) => visible.has(key), [visible]);

	const toggle = useCallback(
		(key: DashboardColumnKey) => {
			// A promoted column is visible for a reason the stored preference knows
			// nothing about, so hiding one has to cancel the promotion as well as the
			// preference — otherwise the promotion puts it straight back and the menu
			// item reads as dead.
			const promotable = resolvedView && DASHBOARD_RESOLVED_VIEW_EXTRA_DEFAULTS.has(key);

			if (visible.has(key)) {
				if (promotable) setSuppressed(prev => new Set([...prev, key]));
				if (persisted.visible.has(key)) persisted.toggle(key);
				return;
			}

			// Showing it again: lifting the suppression is enough while the view still
			// promotes the column, and leaves the stored preference untouched.
			if (promotable) {
				setSuppressed(prev => {
					const next = new Set(prev);
					next.delete(key);
					return next;
				});
				return;
			}
			persisted.toggle(key);
		},
		[resolvedView, visible, persisted]
	);

	return useMemo(() => ({visible, isVisible, toggle}), [visible, isVisible, toggle]);
}
